import { Skeleton } from "@/components/ui/skeleton";

/**
 * Full-page loading state for the dashboard-first launcher, shown while
 * `/api/session` is in flight.
 *
 * Mirrors `LauncherPage`'s real layout — ambient wash, header row, greeting,
 * recents grid, then the dashboard body — so data landing doesn't shift
 * anything. Before this, `AppShell` painted a bare background during the
 * session fetch, so a cold load went blank → fully-populated with no
 * intermediate state, and the per-dashboard skeletons never got a chance to
 * show.
 *
 * **Deliberately fades in after a beat** rather than painting immediately.
 * Two reasons, both real:
 *
 *  1. On a 401 `lib/api.ts` redirects to `/api/auth/start` and the session
 *     promise never resolves, so `isLoading` stays true through the whole
 *     bounce. Painting instantly would flash authenticated-looking chrome at
 *     a signed-out visitor — the thing `AppShell`'s blank-screen gate exists
 *     to prevent.
 *  2. A fast session shouldn't flash a skeleton at all; appearing for 80ms
 *     and vanishing reads as a glitch.
 *
 * Done in CSS (`fill-mode-backwards` holds opacity 0 through the delay)
 * rather than a `setTimeout` + state, which would need an effect that sets
 * state — something the `react-hooks/set-state-in-effect` lint rule
 * disallows, and which this doesn't need.
 */
export function LauncherSkeleton() {
  return (
    <div
      className="animate-in fade-in-0 relative flex min-h-screen flex-col overflow-hidden bg-background text-foreground delay-300 duration-200 fill-mode-backwards"
      // Announced rather than silent: a screen-reader user gets "Loading"
      // instead of nothing at all while the page is empty.
      role="status"
      aria-label="Loading your dashboard"
    >
      {/* Same ambient wash the real page paints, so the background doesn't
          pop in when the session lands. Decorative. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 [html.light_&]:hidden"
        style={{
          background:
            "radial-gradient(60% 50% at 18% -10%, var(--primary-dim), transparent 65%)," +
            "radial-gradient(50% 40% at 100% 0%, var(--orange-dim), transparent 60%)",
        }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 hidden [html.light_&]:block"
        style={{
          background:
            "radial-gradient(60% 50% at 18% -10%, var(--primary-mid), transparent 65%)," +
            "radial-gradient(50% 40% at 100% 0%, var(--orange-mid), transparent 60%)",
        }}
      />

      {/* Header: the wordmark is a real asset, not a skeleton — it's already
          loaded and showing it immediately makes the load feel anchored
          rather than entirely blank. */}
      <header className="relative flex items-center justify-between px-6 py-5 md:px-10">
        <img
          src={`${import.meta.env.BASE_URL}logo-full-dark.svg`}
          alt=""
          className="block h-auto w-[140px] max-w-full [html.light_&]:hidden"
        />
        <img
          src={`${import.meta.env.BASE_URL}logo-full-light.svg`}
          alt=""
          className="hidden h-auto w-[140px] max-w-full [html.light_&]:block"
        />
        <div className="flex items-center gap-2">
          <Skeleton className="h-9 w-9 rounded-md" />
          <Skeleton className="h-9 w-9 rounded-full" />
        </div>
      </header>

      <div className="relative flex flex-1 flex-col px-5 pb-8 pt-6 md:px-7 md:pt-7 lg:px-8">
        {/* Greeting: two lines matching "Welcome back, X" + the h1. */}
        <div className="mb-4 flex flex-col gap-2">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-7 w-72" />
        </div>

        {/* Recents panel — same card chrome and 4-across grid as
            `RecentsPanel`, so the widget doesn't resize on arrival. */}
        <div className="mb-5 flex flex-col overflow-hidden rounded-xl border border-border bg-card">
          <div className="flex items-center gap-2 px-4 pb-3 pt-4">
            <Skeleton className="h-4 w-4 rounded" />
            <Skeleton className="h-3 w-36" />
          </div>
          <div className="grid grid-cols-1 gap-3 px-4 pb-4 sm:grid-cols-2 xl:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="flex flex-col gap-2 rounded-lg border border-border/60 p-3"
              >
                <div className="flex items-center justify-between">
                  <Skeleton className="h-7 w-7 rounded-md" />
                  <Skeleton className="h-3 w-12" />
                </div>
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-20" />
              </div>
            ))}
          </div>
        </div>

        {/* Dashboard body: a KPI strip over two chart cards. Matches the
            admin/ops dashboards, which are what most callers land on; the
            role dashboards differ in detail but share this silhouette, and
            each swaps in its own skeletons once the session resolves. */}
        <div className="mb-4 flex flex-wrap gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="flex min-w-[150px] flex-1 flex-col justify-between gap-4 rounded-lg border border-border bg-card px-5 py-4 shadow-card"
            >
              <div className="flex items-center justify-between gap-2">
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-7 w-7 rounded-md" />
              </div>
              <div className="flex flex-col gap-1">
                <Skeleton className="h-7 w-16" />
                <Skeleton className="h-3 w-24" />
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {Array.from({ length: 2 }).map((_, i) => (
            <div
              key={i}
              className="flex flex-col gap-4 rounded-lg border border-border bg-card p-5 shadow-card"
            >
              <Skeleton className="h-3 w-32" />
              <div className="flex flex-col gap-2.5">
                {Array.from({ length: 3 }).map((_, j) => (
                  <div key={j} className="flex flex-col gap-1.5">
                    <div className="flex justify-between">
                      <Skeleton className="h-3 w-20" />
                      <Skeleton className="h-3 w-8" />
                    </div>
                    <Skeleton className="h-1.5 w-full rounded-full" />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
